import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, MoreHorizontal, RefreshCw } from 'lucide-react';
import {
  useDeleteStudentGroup, useGroupingSuggestions, useRefreshStudentGroup, useStudentGroupsList,
} from '../../../api/hooks';
import type { GroupingBand, GroupingLens, GroupingStudent, StudentGroup } from '../../../api/types';
import { Button, Chip, ConfirmDialog, EmptyState, PagedFooter, PanelCard, Segmented, ShowMoreFooter } from '../../../components/ui';
import Spinner from '../../../components/ui/Spinner';
import ErrorPanel from '../../../components/ui/ErrorPanel';
import { FaUsers } from '../../../components/ui/icons';
import StudentGroupForm from '../../settings/components/StudentGroupForm';
import { dayMonth, dayMonthTime } from '../../../lib/dates';
import { usePagination } from '../../../lib/usePagination';
import { useTaughtSubjects } from '../../../lib/teaching';

interface Props {
  gradeId: number;
  sectionId: number | 'all';
  subjectId: number | 'all';
  weeks?: number;
  /** "Mathematics", for the score-level group names. */
  subjLabel?: string;
  /** Offered by the empty state's "Show ..." buttons. */
  suggestSubject?: { id: number; name: string };
  onPickSubject?: (id: number) => void;
  suggestSection?: { id: number; name: string };
  onPickSection?: (id: number) => void;
}

const LENSES: Array<{ key: GroupingLens; label: string }> = [
  { key: 'chapter', label: 'By chapter' },
  { key: 'level', label: 'By score level' },
  { key: 'readiness', label: 'National readiness' },
];
const LENS_LABEL: Record<string, string> = { chapter: 'Chapter', level: 'Score level', readiness: 'National readiness' };
const NOT_ENOUGH = 'not_enough';
const ROWS_SHOWN = 8;
const KINDS: Array<{ key: 'homework' | 'worksheet' | 'exam'; label: string }> = [
  { key: 'homework', label: 'Homework' }, { key: 'worksheet', label: 'Worksheet' }, { key: 'exam', label: 'Exam' },
];

// The band's tone, from weakest to strongest; "Not enough data" stays neutral.
const BAND_TONE: Record<string, 'down' | 'warn' | 'info' | 'up' | 'accent' | 'neutral'> = {
  reteach: 'down', practise: 'warn', secure: 'up',
  support: 'down', core: 'info', extension: 'up',
  not_ready: 'down', borderline: 'warn', on_track: 'info', strong: 'up',
  not_enough: 'neutral',
};

// Design `.opt` / `.opt.on` pills (same look as the assign pills).
const pill = (on: boolean) =>
  `inline-flex min-h-[36px] items-center gap-1.5 rounded-full border px-[13px] text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
    on ? 'border-navy bg-navy text-white' : 'border-line-strong bg-surface text-ink hover:border-slate'}`;
const eyebrow = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';
const menuItem = 'flex w-full items-center gap-2 px-4 py-2 text-left text-[13px] font-medium text-ink hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none';

/** A button that opens a small menu; closes on pick, outside click or Escape. */
function MenuButton({ label, trigger, children, triggerClass }: {
  label: string; trigger: ReactNode; triggerClass?: string; children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-label={label} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((o) => !o)}
        className={triggerClass ?? 'inline-flex h-8 w-8 items-center justify-center rounded-[8px] text-muted hover:bg-surface-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent'}>
        {trigger}
      </button>
      {open && (
        <div role="menu" aria-label={label} className="absolute right-0 top-full z-50 mt-1 min-w-[190px] rounded-[12px] border border-line bg-surface py-1 shadow-card">
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

const byValue = (a: GroupingStudent, b: GroupingStudent) => {
  if (a.value == null && b.value != null) return 1;
  if (b.value == null && a.value != null) return -1;
  if (a.value != null && b.value != null && a.value !== b.value) return a.value - b.value;
  return a.name.localeCompare(b.name);
};

function BandCard({ band, bands, onMove, onSave }: {
  band: GroupingBand; bands: GroupingBand[];
  onMove: (studentId: number, toKey: string) => void;
  onSave?: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? band.students : band.students.slice(0, ROWS_SHOWN);
  const others = bands.filter((b) => b.key !== band.key);
  return (
    <section aria-label={band.label} className="flex min-w-0 flex-col gap-2.5 rounded-[16px] border border-line p-3.5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="font-semibold text-ink">{band.label}</h3>
        <Chip tone={BAND_TONE[band.key] ?? 'neutral'}>{band.students.length}</Chip>
      </div>
      <p className="text-xs text-muted">{band.description}</p>
      {band.students.length === 0 ? (
        <p className="text-xs text-muted">No students.</p>
      ) : (
        <ul className="divide-y divide-hairline">
          {shown.map((s) => (
            <li key={s.id} className="flex items-center gap-2 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-ink">{s.name}</div>
                <div className="text-xs text-muted">{s.reason}</div>
              </div>
              <MenuButton label={`Move ${s.name}`} trigger={<MoreHorizontal size={18} strokeWidth={1.8} aria-hidden />}>
                {(close) => others.map((b) => (
                  <button key={b.key} type="button" role="menuitem" className={menuItem}
                    onClick={() => { close(); onMove(s.id, b.key); }}>
                    Move to {b.label}
                  </button>
                ))}
              </MenuButton>
            </li>
          ))}
        </ul>
      )}
      {band.students.length > ROWS_SHOWN && (
        <ShowMoreFooter shown={shown.length} total={band.students.length} expanded={expanded} onToggle={() => setExpanded((e) => !e)} />
      )}
      {onSave && band.students.length > 0 && (
        <div className="mt-auto pt-1">
          <Button variant="ghost" size="sm" onClick={onSave}>Save as group</Button>
        </div>
      )}
    </section>
  );
}

function SavedGroupRow({ group, sectionId, canAssign, onAssign, onEdit }: {
  group: StudentGroup; sectionId: number; canAssign: boolean;
  onAssign: (kind: string) => void; onEdit: () => void;
}) {
  const refreshM = useRefreshStudentGroup(sectionId, group.id);
  const deleteM = useDeleteStudentGroup(sectionId, group.id);
  const [confirming, setConfirming] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const size = group.size ?? group.members.length;
  const band = group.criteria?.band;
  const canRefresh = !!group.can_edit && !!group.lens && !!band && band !== NOT_ENOUGH;
  const meta = [
    `Made ${dayMonth(group.created_at)}${group.created_by_name ? ` by ${group.created_by_name}` : ''}`,
    group.refreshed_at ? `refreshed ${dayMonthTime(group.refreshed_at)}` : null,
  ].filter(Boolean).join(' · ');
  async function refresh() {
    setNote(null);
    try {
      const r = await refreshM.mutateAsync();
      setNote(`Refreshed: ${r.added} added, ${r.removed} removed.`);
    } catch (err: any) {
      setNote(err?.response?.data?.detail ?? 'Refresh failed.');
    }
  }
  return (
    <li className="flex flex-wrap items-center gap-3 border-b border-hairline py-[11px] last:border-b-0">
      <div className="min-w-0 flex-1 basis-60">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-ink">{group.name}</span>
          {group.lens ? <Chip tone="info">{LENS_LABEL[group.lens] ?? group.lens}</Chip> : <Chip tone="mute">Hand-picked</Chip>}
          <span className="font-mono text-xs text-muted">{size} {size === 1 ? 'student' : 'students'}</span>
        </div>
        <div className="text-xs text-muted">{meta}</div>
        {group.notes && <div className="mt-0.5 text-xs text-muted">{group.notes}</div>}
        {note && <div role="status" className="mt-0.5 text-xs text-muted-2">{note}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {canAssign && (
          <MenuButton label={`Assign to ${group.name}`}
            triggerClass="inline-flex min-h-[36px] items-center gap-1.5 rounded-[12px] border border-line-strong bg-surface px-3 text-[13px] font-semibold text-ink hover:border-slate focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            trigger={<>Assign<ChevronDown size={16} strokeWidth={1.8} aria-hidden /></>}>
            {(close) => KINDS.map((k) => (
              <button key={k.key} type="button" role="menuitem" className={menuItem} onClick={() => { close(); onAssign(k.key); }}>
                {k.label}
              </button>
            ))}
          </MenuButton>
        )}
        {group.can_edit && <Button variant="ghost" size="sm" onClick={onEdit}>Edit</Button>}
        {canRefresh && (
          <Button variant="ghost" size="sm" loading={refreshM.isPending} icon={<RefreshCw size={16} strokeWidth={1.8} aria-hidden />} onClick={refresh}>
            Refresh
          </Button>
        )}
        {group.can_edit && <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>Delete</Button>}
      </div>
      <ConfirmDialog
        isOpen={confirming}
        title="Delete this group?"
        body="It will be archived. Work already given to its students stays with them."
        confirmLabel="Delete"
        danger
        onConfirm={() => { deleteM.mutate(); setConfirming(false); }}
        onCancel={() => setConfirming(false)}
      />
    </li>
  );
}

function SavedGroups({ gradeId, sectionId, subjectId }: { gradeId: number; sectionId: number; subjectId: number }) {
  const q = useStudentGroupsList(sectionId, subjectId);
  const navigate = useNavigate();
  const taught = useTaughtSubjects();
  const [editId, setEditId] = useState<number | null>(null);
  const groups = q.data?.groups ?? [];
  const pg = usePagination(groups, 10, `${sectionId}-${subjectId}`);
  // Assign shows to whoever can give work for this subject in this class (owner or not).
  const subjects = taught.forGrade(gradeId);
  const sections = taught.sectionsFor(gradeId, subjectId);
  const canAuthor = (subjects == null || subjects.includes(subjectId)) && (sections == null || sections.includes(sectionId));
  const editing = groups.find((g) => g.id === editId);
  return (
    <PanelCard title="Saved groups" subtitle="Groups of this class for this subject. Students never see group names.">
      {q.isLoading ? <div className="flex justify-center py-6"><Spinner size="md" /></div>
        : q.isError ? <ErrorPanel />
        : groups.length === 0 ? <p className="text-sm text-muted">No saved groups yet. Save a suggested group above.</p>
        : (
          <>
            <ul>
              {pg.pageItems.map((g) => (
                <SavedGroupRow key={g.id} group={g} sectionId={sectionId}
                  canAssign={canAuthor}
                  onEdit={() => setEditId(g.id)}
                  onAssign={(kind) => navigate(`/exams/add-exam?kind=${kind}&grade=${gradeId}&subject=${subjectId}&group=${g.id}`)} />
              ))}
            </ul>
            {groups.length > 10 && (
              <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Saved groups" divider="list" />
            )}
          </>
        )}
      {editing && <StudentGroupForm sectionId={sectionId} existing={editing} onClose={() => setEditId(null)} />}
    </PanelCard>
  );
}

export default function GroupingTab({ gradeId, sectionId, subjectId, weeks, subjLabel, suggestSubject, onPickSubject, suggestSection, onPickSection }: Props) {
  const [lens, setLens] = useState<GroupingLens>('chapter');
  // The teacher's chapter pick, tied to the class it was made in so a section or
  // subject switch never sends a chapter of the old one. null = the server's pick
  // (the weakest chapter).
  const scopeKey = `${gradeId}:${sectionId}:${subjectId}`;
  const [chapterPick, setChapterPick] = useState<{ scope: string; id: number } | null>(null);
  const chapter = chapterPick?.scope === scopeKey ? chapterPick.id : null;
  const setChapter = (id: number) => setChapterPick({ scope: scopeKey, id });
  // Students the teacher moved: student id -> band key. Cleared whenever the suggestions change.
  const [moves, setMoves] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const q = useGroupingSuggestions({
    grade: gradeId, section: sectionId, subject: subjectId, lens,
    chapter: lens === 'chapter' && chapter != null ? chapter : undefined, weeks,
  });
  const d = q.data;
  useEffect(() => { setMoves({}); }, [d]);

  const bands = useMemo<GroupingBand[]>(() => {
    if (!d) return [];
    if (Object.keys(moves).length === 0) return d.groups;
    const all = d.groups.flatMap((g) => g.students.map((s) => ({ s, key: moves[s.id] ?? g.key })));
    return d.groups.map((g) => ({ ...g, students: all.filter((x) => x.key === g.key).map((x) => x.s).sort(byValue) }));
  }, [d, moves]);

  if (sectionId === 'all' || subjectId === 'all') {
    const actions = [
      sectionId === 'all' && suggestSection && onPickSection
        ? <Button key="sec" variant="ghost" onClick={() => onPickSection(suggestSection.id)}>{`Show section ${suggestSection.name}`}</Button> : null,
      subjectId === 'all' && suggestSubject && onPickSubject
        ? <Button key="sub" variant="ghost" onClick={() => onPickSubject(suggestSubject.id)}>{`Show ${suggestSubject.name}`}</Button> : null,
    ].filter(Boolean);
    return (
      <PanelCard>
        <EmptyState icon={<FaUsers />} title="Pick a section and a subject"
          body="Groups are made for one class and one subject at a time. Choose both above."
          action={actions.length ? <div className="flex flex-wrap justify-center gap-2">{actions}</div> : undefined} />
      </PanelCard>
    );
  }

  const activeChapter = d?.chapter_id ?? chapter;
  const chapterName = d?.chapters?.find((c) => c.id === activeChapter)?.name;
  const groupName = (b: GroupingBand) => {
    if (lens === 'chapter') return `${chapterName ?? 'Chapter'} · ${b.label}`;
    if (lens === 'level') return `${subjLabel && subjLabel !== 'Subject' ? subjLabel : 'Subject'} · ${b.label}`;
    return `National · ${b.label}`;
  };
  const savingBand = bands.find((b) => b.key === saving);

  let body: ReactNode;
  if (q.isLoading) body = <div className="flex justify-center py-8"><Spinner size="md" /></div>;
  else if (q.isError || !d) body = <ErrorPanel />;
  else if (!d.available) body = <EmptyState icon={<FaUsers />} title="No suggestions for this lens" body={d.message ?? 'There is not enough data to suggest groups yet.'} />;
  else body = (
    <>
      {lens === 'chapter' && (d.chapters?.length ?? 0) > 0 && (
        <div className="mb-4">
          <p className={`mb-2 ${eyebrow}`}>Chapter</p>
          <div role="group" aria-label="Chapter" className="flex flex-wrap gap-2">
            {d.chapters!.map((c) => (
              <button key={c.id} type="button" aria-pressed={c.id === activeChapter} className={pill(c.id === activeChapter)} onClick={() => setChapter(c.id)}>
                <span>{c.name}</span>
                {c.class_pct != null && <span className="font-mono opacity-75">{Math.round(c.class_pct)}%</span>}
              </button>
            ))}
          </div>
        </div>
      )}
      {lens === 'chapter' && (d.chapters?.length ?? 0) === 0 ? (
        <EmptyState icon={<FaUsers />} title="No chapter answers yet" body="No answers were tagged to a chapter of this subject in the window." />
      ) : (
        <>
          {lens === 'readiness' && d.national_exam && (
            <p className="mb-3 text-[13px] text-muted">Against the predicted chapters of <span className="font-semibold text-ink">{d.national_exam.name}</span>.</p>
          )}
          <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${bands.length > 4 ? 'min-[1181px]:grid-cols-5' : 'min-[1181px]:grid-cols-4'}`}>
            {bands.map((b) => (
              <BandCard key={b.key} band={b} bands={bands}
                onMove={(id, to) => setMoves((m) => ({ ...m, [id]: to }))}
                onSave={() => setSaving(b.key)} />
            ))}
          </div>
        </>
      )}
    </>
  );

  return (
    <>
      <PanelCard title="Suggested groups" help="tab_grouping" align="end"
        subtitle="Every student shows why they are in a group. Move students, then save a group to give it its own work."
        actions={<Segmented<GroupingLens> label="Grouping lens" value={lens} onChange={setLens} options={LENSES} />}>
        {body}
      </PanelCard>
      <SavedGroups gradeId={gradeId} sectionId={sectionId} subjectId={subjectId} />
      {savingBand && d && (
        <StudentGroupForm
          key={savingBand.key}
          sectionId={sectionId}
          title="Save as group"
          seed={{ name: groupName(savingBand), member_ids: savingBand.students.map((s) => s.id) }}
          extra={{
            subject_id: subjectId, lens,
            criteria: { lens, band: savingBand.key, ...(lens === 'chapter' ? { chapter_id: activeChapter ?? null } : {}), weeks: d.weeks },
          }}
          onClose={() => setSaving(null)} />
      )}
    </>
  );
}
